import { type FC } from 'react';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { PublicTopBar } from './PublicTopBar';
import { cn } from '@/lib/utils';

/**
 * LoginShell — the Agentic Loan Audit sign-in surface.
 *
 * Flat, centered marketing hero: shared public top bar, an eyebrow pill, the
 * Loan-Audit / Automated headline, and two CTAs. Both CTAs start the Office
 * 365 sign-in. Receives `onMicrosoftLogin` so the shell stays decoupled from
 * the MSAL wiring (routes/login.tsx supplies it).
 */

export interface LoginShellProps {
  onMicrosoftLogin: () => void;
  productName?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
}

const MicrosoftTiles: FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden className="shrink-0">
    <rect x="0" y="0" width="8" height="8" fill="#F25022" />
    <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
    <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
    <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
  </svg>
);

export const LoginShell: FC<LoginShellProps> = ({
  onMicrosoftLogin,
  isLoading = false,
  errorMessage,
}) => {
  return (
    <div
      className="min-h-[100dvh] flex flex-col bg-background"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <PublicTopBar onGetStarted={onMicrosoftLogin} isLoading={isLoading} />

      {/* Centered hero */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-3xl mx-auto flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant bg-secondary-container/50 px-3 py-1 text-micro font-semibold uppercase tracking-[0.08em] text-primary">
            <ShieldCheck className="w-3.5 h-3.5" />
            Enterprise AI compliance
          </span>

          <h1 className="mt-6 font-display font-black leading-[1.04] tracking-tight text-[clamp(2.75rem,7vw,4.75rem)]">
            <span className="text-on-surface">Loan Audit.</span>
            <br />
            <span className="text-brand">Automated.</span>
          </h1>

          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={onMicrosoftLogin}
              disabled={isLoading}
              className={cn(
                'min-h-12 px-5 flex items-center justify-center gap-3 rounded-xl',
                'border border-outline-variant bg-surface-container-lowest',
                'text-on-surface text-body font-medium',
                'hover:bg-surface-container-low hover:border-outline transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <MicrosoftTiles />
              <span>{isLoading ? 'Signing in…' : 'Continue with Office 365'}</span>
            </button>
            <button
              onClick={onMicrosoftLogin}
              disabled={isLoading}
              className={cn(
                'min-h-12 px-6 flex items-center justify-center gap-2 rounded-xl',
                'bg-primary text-on-primary text-body font-semibold',
                'hover:bg-primary-container transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <span>Get Started</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {errorMessage && (
            <div className="mt-5 max-w-md px-3 py-2 rounded-lg bg-error/10 border border-error/30 text-caption text-error">
              {errorMessage}
            </div>
          )}
        </div>
      </main>

      <footer className="shrink-0 border-t border-outline-variant">
        <div className="max-w-6xl mx-auto w-full px-6 py-4 text-center text-micro text-on-surface-variant">
          © 2026 Applied Cloud Computing Private Limited
        </div>
      </footer>
    </div>
  );
};

export default LoginShell;
